import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy } from '@ngneat/until-destroy';
import {
  AppCalculator,
  AssetConditionValue,
  AssetSelectionComponentValue,
  BalloonPaymentValue,
  BalloonPaymentValueOptions,
  BrokerageSelectionValue,
  BusinessSearchResultValue,
  BusinessSearchValue,
  calculateAssetFinanceEstimation,
  CurrencyInputValue,
  FinanceTypeValue,
  FinanceTypeValueOptions,
  LoanTermValue,
  minDepositForAssetFinance,
  PaymentFrequencyValue,
  Quote,
  RatecardDetails,
  RedbookSearch,
  SearchCompanyByABNResult,
  TotalPaymentBreakupDialogData,
  TransactionValue,
  validateEOTAge,
  getMaxLoanTermForTier,
  YesNoValue,
  ASSET_FINANCE_MAX_BROKERAGE_PERCENT,
} from '@portal-workspace/grow-shared-library';
import {
  AggregatorSearchFn, ApplicationDialogService, ASSET_FINANCE_OPTIONS_FILTER, AssetSelectionComponentSearchFn, BalloonPaymentComponent, BrokerageSelectionComponent, doMarkAll,
  linkEquifaxDirectorScoreControls, markTriggerSubject, PortalHotToastService, setupUntilDestroy
} from '@portal-workspace/grow-ui-library';
import { Moment } from 'moment';
import numeral from 'numeral';
import { combineLatest, of, Subscription, Subject } from 'rxjs';
import { map, startWith, tap } from 'rxjs/operators';
import { ApplicationService } from '../../service/application.service';
import { navigationUrlForApplications } from '../../service/navigation-urls';
import { RedbookService } from '../../service/redbook.service';

import { FlexModule } from '@angular/flex-layout/flex';
import { createTwoDecimalInputMask, BusinessSearchComponent, BusinessSearchFn, AssetConditionComponent, AssetSelectionComponent, BalloonPaymentComponent as BalloonPaymentComponent_1, CurrencyInputComponent, FinanceTypeSelectionComponent, LoanTermsSelectionComponent, MarkDirective, PaymentFrequencySelectionComponent, TransactionTypeSelectionComponent, YesNoComponent } from '@portal-workspace/grow-ui-library';
import { InputMaskModule } from '@ngneat/input-mask';
import { MatInputModule } from '@angular/material/input';

@UntilDestroy({arrayName: 'subscriptions'})
@Component({
    templateUrl: './asset-finance-calculator.page.html',
    styleUrls: ['asset-finance-calculator.page.scss'],
    standalone: true,
    imports: [CommonModule, MatButtonToggleModule, MatInputModule, InputMaskModule, BusinessSearchComponent, FlexModule, FormsModule, ReactiveFormsModule, CurrencyInputComponent, MarkDirective, AssetSelectionComponent, AssetConditionComponent, YesNoComponent, LoanTermsSelectionComponent, PaymentFrequencySelectionComponent, FinanceTypeSelectionComponent, BalloonPaymentComponent_1, BrokerageSelectionComponent, MatButtonModule, TransactionTypeSelectionComponent],
})
export class AssetFinanceCalculatorPage implements OnInit, AfterViewInit {
  
  // Helper method for template
  parseAssetYear(year: string | null | undefined): number | null {
    return year ? parseInt(year) : null;
  }
  ASSET_FINANCE_OPTIONS_FILTER = ASSET_FINANCE_OPTIONS_FILTER;
  ASSET_FINANCE_MAX_BROKERAGE_PERCENT = ASSET_FINANCE_MAX_BROKERAGE_PERCENT;

  inputMaskTwoDecimal = createTwoDecimalInputMask();

  MIN_DEPOSIT = 2000;
  maxLoanTerm: number = 84;

  depositMin: number | null = null;
  depositMax: number | null = null;
  assetCategoryIndexFilter = ['137', '132','130','140','136','133','134','106','135','142'];


  subscriptions: Subscription[] = [];

  markTriggerSubject = markTriggerSubject;
  markTriggerSubjectInterest = new Subject<boolean>();

  formGroupCalculator!: FormGroup <{
      assetCondition: FormControl<AssetConditionValue>,
      invoiceAmount: FormControl<CurrencyInputValue>,
      depositAmount: FormControl<CurrencyInputValue>,
      loanAmount: FormControl<CurrencyInputValue>,
      asset: FormControl<AssetSelectionComponentValue>,
      loanTerms: FormControl<LoanTermValue>,
      paymentFrequency: FormControl<PaymentFrequencyValue>,
      financeType: FormControl<FinanceTypeValue>,
      docFeeFinanced: FormControl<YesNoValue>,
      balloonPayment: FormControl<BalloonPaymentValue>,
      brokerOriginationFee: FormControl<CurrencyInputValue>,
      brokerage: FormControl<BrokerageSelectionValue>,
      propertyOwner: FormControl<YesNoValue>,
      adverseOnFile: FormControl<YesNoValue>,
      equifaxScoreAboveThreshold: FormControl<YesNoValue>,
      equifaxDirectorScoreAbove700: FormControl<YesNoValue>,
      transactionType: FormControl<TransactionValue>,
      business: FormControl<BusinessSearchValue>,
      abnAge: FormControl<number | null>,
  }>;
  BrokerSearchFn!: AggregatorSearchFn;
  businessSearchFn!: BusinessSearchFn;
  formControlInvoiceAmount!: FormControl<CurrencyInputValue>;
  formControlDepositAmount!: FormControl<CurrencyInputValue>;
  formControlLoanAmount!: FormControl<CurrencyInputValue>;
  formControlAsset!: FormControl<AssetSelectionComponentValue>;
  formControlBusiness!: FormControl<BusinessSearchValue>;
  AssetSearchFn!: AssetSelectionComponentSearchFn;
  formControlAssetCondition!: FormControl<AssetConditionValue>;
  formControlLoanTerms!: FormControl<LoanTermValue>;
  formControlPaymentFrequency!: FormControl<PaymentFrequencyValue>;
  formControlFinanceType!: FormControl<FinanceTypeValue>;
  formControlDocFeeFinanced!: FormControl<YesNoValue>;
  formControlBalloonPayment!: FormControl<BalloonPaymentValue>;
  formControlBrokerOriginationFee!: FormControl<CurrencyInputValue>;
  formControlBrokerage!: FormControl<BrokerageSelectionValue>;
  formControlPropertyOwner!: FormControl<YesNoValue>;
  formControlAdverseOnFile!: FormControl<YesNoValue>;
  formControlEquifaxScoreAboveThreshold!: FormControl<YesNoValue>;
  formControlEquifaxDirectorScoreAbove700!: FormControl<YesNoValue>;
  formControlAbnAge!: FormControl<number | null>;
  formControlTransactionType!: FormControl<TransactionValue>;
  formControlQuoteType!: FormControl<string | null>;
  formControlInterestRate!: FormControl<number | null>;
  
  // Form controls for Interest Rate section
  formControlFinanceTypeInterest!: FormControl<FinanceTypeValue>;
  formControlDocFeeFinancedInterest!: FormControl<YesNoValue>;
  formControlLoanTermsInterest!: FormControl<LoanTermValue>;
  formControlPaymentFrequencyInterest!: FormControl<PaymentFrequencyValue>;
  formControlInvoiceAmountInterest!: FormControl<CurrencyInputValue>;
  formControlDepositAmountInterest!: FormControl<CurrencyInputValue>;
  formControlLoanAmountInterest!: FormControl<CurrencyInputValue>;
  formControlBalloonPaymentInterest!: FormControl<BalloonPaymentValue>;
  formControlBrokerOriginationFeeInterest!: FormControl<CurrencyInputValue>;
  formControlBrokerageInterest!: FormControl<BrokerageSelectionValue>;

  rateCard!: RatecardDetails;
  @ViewChild('balloonPaymentComponent') balloonPaymentComponent!: BalloonPaymentComponent;

  constructor(
    private formBuilder: FormBuilder,
    private toastService: PortalHotToastService,
    private applicationDialogService: ApplicationDialogService,
    private activatedRoute: ActivatedRoute,
    private redbookService: RedbookService,
    private appService: ApplicationService,
    private router: Router) {

    this.initCalculator();
  }

  ngOnInit() {
    setupUntilDestroy(this);
    this.rateCard = (this.activatedRoute.snapshot.data as any).ratecard;

    // Setup business search function
    this.businessSearchFn = this.appService.businessSearchFn;
    
    // Watch for business search changes to update ABN age field
    this.subscriptions.push(this.formControlBusiness.valueChanges.pipe(
      tap((businessValue: BusinessSearchValue) => {
        if (businessValue && businessValue.type === 'search-result' && businessValue.result) {
          const abnAgeMonths = businessValue.result.ABNAgeMonths ?? 0;
          // Auto-populate the ABN age field from lookup
          this.formControlAbnAge.setValue(abnAgeMonths);
          // Disable the field since it's auto-populated from business lookup
          this.formControlAbnAge.disable();
          console.log(`ABN Age from lookup: ${abnAgeMonths} months (field disabled)`);
        } else {
          // Clear ABN age field when business search is cleared
          this.formControlAbnAge.setValue(null);
          // Enable the field for manual entry
          this.formControlAbnAge.enable();
        }
      })
    ).subscribe());
    this.subscriptions.push(this.formControlInvoiceAmount.valueChanges.pipe(
      tap(r => {
       this.updateLoanAmount();
      })
    ).subscribe());
    this.subscriptions.push(this.formControlDepositAmount.valueChanges.pipe(
      tap(r => {
        this.updateLoanAmount();
    })).subscribe());

    // deposit calculation trigger
    this.subscriptions.push(this.formControlInvoiceAmount.valueChanges.pipe(
      tap(r => {
        this.updateDepositValidation();
      })
    ).subscribe());
    this.subscriptions.push(this.formControlPropertyOwner.valueChanges.pipe(
      tap(r => {
        this.updateDepositValidation();
      })
    ).subscribe());
    this.subscriptions.push(...linkEquifaxDirectorScoreControls(
      this.formControlEquifaxScoreAboveThreshold,
      this.formControlEquifaxDirectorScoreAbove700,
    ));
    this.subscriptions.push(this.formControlAsset.valueChanges.pipe(
      tap(r => {
        this.updateDepositValidation();
        // When Solar System is selected, set Finance Type to Chattel Mortgage and disable
        if (r && r.category && r.category.index === '106') {
          this.formControlFinanceType.disable();
          const chattelMortgageOption = FinanceTypeValueOptions.find(o => o.type === 'chattel-mortgage');
          this.formControlFinanceType.setValue(chattelMortgageOption ?? null);
        } else {
          this.formControlFinanceType.enable();
        }
        // Restrict max loan term based on asset tier
        if (r && r.category && r.category.index) {
          this.maxLoanTerm = getMaxLoanTermForTier(r.category.index);
        } else {
          this.maxLoanTerm = 84;
        }
      })
    ).subscribe());

    // Enhanced loan amount calculation with GST handling for rental (Interest section)
    const subscriptionInterest = combineLatest([
      this.formControlInvoiceAmountInterest.valueChanges,
      this.formControlDepositAmountInterest.valueChanges,
      this.formControlFinanceTypeInterest.valueChanges,
    ]).pipe(
      tap(r => {
        const invoiceAmount = r[0] ?? 0;
        const depositAmount = r[1] ?? 0;
        const financeType = r[2];
        const depositAmountExGST = Math.round(depositAmount * 1000 / 11) / 100;
        const deposit = financeType?.type === 'rental' ? depositAmountExGST : depositAmount;
        const loanAmount = parseFloat((invoiceAmount - deposit > 0) ? (invoiceAmount - deposit).toFixed(2) : '0');
        this.formControlLoanAmountInterest.setValue(loanAmount);
      })
    ).subscribe();
    this.subscriptions.push(subscriptionInterest);

    // Balloon disabled for rental OR loan term > 60 months (Interest section - finance type changes)
    const subscriptionInterestRental = this.formControlFinanceTypeInterest.valueChanges.pipe(
      tap((financeType: FinanceTypeValue) => {
        const isRental = financeType?.type === 'rental';
        const loanTermValue = this.formControlLoanTermsInterest.value;
        const isLongTerm = loanTermValue && parseInt(loanTermValue.type) > 60;
        if (isRental || isLongTerm) {
          this.formControlBalloonPaymentInterest.setValue(BalloonPaymentValueOptions[0]);
          this.formControlBalloonPaymentInterest.disable();
        } else {
          this.formControlBalloonPaymentInterest.enable();
        }
      })
    ).subscribe();
    this.subscriptions.push(subscriptionInterestRental);

    // Toggle validation based on quote type (application vs interest)
    const applicationFormControls = [
      this.formControlAssetCondition,
      this.formControlInvoiceAmount,
      this.formControlDepositAmount,
      this.formControlLoanAmount,
      this.formControlAsset,
      this.formControlLoanTerms,
      this.formControlPaymentFrequency,
      this.formControlFinanceType,
      this.formControlDocFeeFinanced,
      this.formControlBalloonPayment,
      this.formControlBrokerOriginationFee,
      this.formControlBrokerage,
      this.formControlPropertyOwner,
      this.formControlAdverseOnFile,
      this.formControlEquifaxScoreAboveThreshold,
      this.formControlEquifaxDirectorScoreAbove700,
      this.formControlTransactionType,
    ];

    const subQuoteType = this.formControlQuoteType.valueChanges.pipe(
      tap((quoteType: string | null) => {
        if (quoteType === 'interest') {
          // Disable application section validators when interest tab is active
          applicationFormControls.forEach(control => {
            control.clearValidators();
            control.updateValueAndValidity();
          });
          console.log('Application section validators disabled');
        } else if (quoteType === 'application') {
          // Re-enable application section validators when application tab is active
          applicationFormControls.forEach(control => {
            control.setValidators([Validators.required]);
            control.updateValueAndValidity();
          });
          console.log('Application section validators enabled');
        }
      })
    ).subscribe();
    this.subscriptions.push(subQuoteType);
  }

  ngAfterViewInit(): void {
    const sub2= combineLatest([
      this.formControlAsset.valueChanges,
      this.formControlLoanTerms.valueChanges
    ]).pipe(
      tap((r: [AssetSelectionComponentValue, LoanTermValue]) => {
        const assetSelectionValue = r[0];
        const loanTermValue = r[1];
        this.updateBalloonPaymentValidation(assetSelectionValue, loanTermValue);
      })
    ).subscribe();
    this.subscriptions.push(sub2);

    // Balloon disabled for rental OR loan term > 60 months (application quote section).
    // startWith ensures this fires when either control changes, even if the other hasn't emitted yet.
    const sub3 = combineLatest([
      this.formControlFinanceType.valueChanges.pipe(startWith(this.formControlFinanceType.value)),
      this.formControlLoanTerms.valueChanges.pipe(startWith(this.formControlLoanTerms.value)),
    ]).pipe(
      tap(([financeType, loanTermValue]: [FinanceTypeValue, LoanTermValue]) => {
        const isRental = financeType?.type === 'rental';
        const isLongTerm = loanTermValue && parseInt(loanTermValue.type) > 60;
        if (isRental || isLongTerm) {
          this.formControlBalloonPayment.setValue(BalloonPaymentValueOptions[0]);
          this.formControlBalloonPayment.disable();
        } else {
          this.formControlBalloonPayment.enable();
        }
      })
    ).subscribe();
    this.subscriptions.push(sub3);

    // Balloon disabled for loan term > 60 months (interest rate quote section)
    const sub3b = this.formControlLoanTermsInterest.valueChanges.pipe(
      tap((loanTermValue: LoanTermValue) => {
        const isRental = this.formControlFinanceTypeInterest.value?.type === 'rental';
        const isLongTerm = loanTermValue && parseInt(loanTermValue.type) > 60;
        if (isRental || isLongTerm) {
          this.formControlBalloonPaymentInterest.setValue(BalloonPaymentValueOptions[0]);
          this.formControlBalloonPaymentInterest.disable();
        } else {
          this.formControlBalloonPaymentInterest.enable();
        }
      })
    ).subscribe();
    this.subscriptions.push(sub3b);

    // Make business search field optional by clearing its validators
    // The business-search component adds required validator internally, but we want it optional
    setTimeout(() => {
      this.formControlBusiness.clearValidators();
      this.formControlBusiness.updateValueAndValidity();
    }, 0);
  }

  updateBalloonPaymentValidation(assetSelectionValue: AssetSelectionComponentValue, loanTermValue: LoanTermValue) {
    if (assetSelectionValue && loanTermValue && assetSelectionValue.category) {
      const assetCatgory = assetSelectionValue.category.index;
      const loanTerm = parseInt(loanTermValue.type);
      // Year is now a text input field (forceSimpleMode), use provided year or current year as fallback
      const assetYear = assetSelectionValue.year ? parseInt(assetSelectionValue.year) : new Date().getFullYear();
      const maxRv = new AppCalculator().getMaxRV(assetCatgory, loanTerm, assetYear);
      this.balloonPaymentComponent.setMaxRv(maxRv);
    }
  }

  updateDepositValidation() {
    const asset: AssetSelectionComponentValue = this.formControlAsset.value;
    if (asset && !asset.invalid && asset.category) {
      const invoiceAmount = this.formControlInvoiceAmount.value ?? 0;
      const assetCategoryIndex = asset.category.index;
      const isPropetyOwner = this.formControlPropertyOwner.value;

      if (invoiceAmount != null && assetCategoryIndex != null && isPropetyOwner != null) {
        const minDeposit = minDepositForAssetFinance(invoiceAmount, isPropetyOwner);
        this.depositMin = minDeposit;
        this.formControlDepositAmount.setValue(minDeposit);
      }
    }
  }

  private updateLoanAmount() {
    const invoiceAmount = this.formControlInvoiceAmount.value;
    const deposit = this.formControlDepositAmount.value;
    if (invoiceAmount != null && deposit != null) {
      let loanAmount = invoiceAmount - deposit;
      loanAmount = loanAmount < 0 ? 0 : loanAmount;
      this.formControlLoanAmount.setValue(loanAmount);
    }
  }

  initCalculator() {
    this.BrokerSearchFn = (term) => {
      return of([
        { abn: 'abn1', entityName: 'Broker1', salesforceId: 'sf1' },
        { abn: 'abn2', entityName: 'Broker2', salesforceId: 'sf2' },
        { abn: 'abn3', entityName: 'Broker3', salesforceId: 'sf3' },
      ]);
    }
    this.formControlAssetCondition = this.formBuilder.control(null, [Validators.required]);
    this.formControlInvoiceAmount = this.formBuilder.control(null, [Validators.required]);
    this.formControlDepositAmount = this.formBuilder.control(null, [Validators.required]);
    this.formControlLoanAmount = this.formBuilder.control(null, [Validators.required]);
    this.formControlAsset = this.formBuilder.control(null, [Validators.required]);
    this.formControlLoanTerms = this.formBuilder.control(null, [Validators.required]);
    this.formControlPaymentFrequency = this.formBuilder.control(null, [Validators.required]);
    this.formControlFinanceType = this.formBuilder.control(null, [Validators.required]);
    this.formControlDocFeeFinanced = this.formBuilder.control(true, [Validators.required]);
    this.formControlBalloonPayment = this.formBuilder.control(BalloonPaymentValueOptions[0],  [Validators.required]);
    this.formControlBrokerOriginationFee = this.formBuilder.control(0, [Validators.required]);
    this.formControlBrokerage = this.formBuilder.control(null, [Validators.required]);
    this.formControlPropertyOwner = this.formBuilder.control(true, [Validators.required]);
    this.formControlAdverseOnFile = this.formBuilder.control(false, [Validators.required]);
    this.formControlEquifaxScoreAboveThreshold = this.formBuilder.control(true, [Validators.required]);
    this.formControlEquifaxDirectorScoreAbove700 = this.formBuilder.control(true, [Validators.required]);
    this.formControlTransactionType = this.formBuilder.control(null, [Validators.required]);
    this.formControlBusiness = this.formBuilder.control(null);
    this.formControlAbnAge = this.formBuilder.control(null);
    this.formControlQuoteType = this.formBuilder.control('application');
    this.formControlInterestRate = this.formBuilder.control(null, [Validators.required]);
    
    // Initialize Interest Rate section form controls
    this.formControlFinanceTypeInterest = this.formBuilder.control(null, [Validators.required]);
    this.formControlDocFeeFinancedInterest = this.formBuilder.control(true, [Validators.required]);
    this.formControlLoanTermsInterest = this.formBuilder.control(null, [Validators.required]);
    this.formControlPaymentFrequencyInterest = this.formBuilder.control(null, [Validators.required]);
    this.formControlInvoiceAmountInterest = this.formBuilder.control(null, [Validators.required]);
    this.formControlDepositAmountInterest = this.formBuilder.control(0, [Validators.required]);
    this.formControlLoanAmountInterest = this.formBuilder.control(null, [Validators.required]);
    this.formControlBalloonPaymentInterest = this.formBuilder.control(BalloonPaymentValueOptions[0], [Validators.required]);
    this.formControlBrokerOriginationFeeInterest = this.formBuilder.control(0, [Validators.required]);
    this.formControlBrokerageInterest = this.formBuilder.control(null, [Validators.required]);

    this.AssetSearchFn = <T extends RedbookSearch>(s: T) => {
      return this.redbookService.search(s).pipe(
        map(r => {
          return r.payload;
        })
      );
    }
    this.formGroupCalculator = this.formBuilder.group({
      assetCondition: this.formControlAssetCondition,
      invoiceAmount: this.formControlInvoiceAmount,
      depositAmount: this.formControlDepositAmount,
      loanAmount: this.formControlLoanAmount,
      asset: this.formControlAsset,
      loanTerms: this.formControlLoanTerms,
      paymentFrequency: this.formControlPaymentFrequency,
      financeType: this.formControlFinanceType,
      docFeeFinanced: this.formControlDocFeeFinanced,
      balloonPayment: this.formControlBalloonPayment,
      brokerOriginationFee: this.formControlBrokerOriginationFee,
      brokerage: this.formControlBrokerage,
      propertyOwner: this.formControlPropertyOwner,
      adverseOnFile: this.formControlAdverseOnFile,
      equifaxScoreAboveThreshold: this.formControlEquifaxScoreAboveThreshold,
      equifaxDirectorScoreAbove700: this.formControlEquifaxDirectorScoreAbove700,
      transactionType: this.formControlTransactionType,
      abnAge: this.formControlAbnAge,
      business: this.formControlBusiness,
      
    });
  }

  onCalculateEstimation(event: Event) {
    markTriggerSubject(this.formGroupCalculator).next(true);
    doMarkAll(this.formGroupCalculator);
    // For asset validation, check if category, type, and year are present
    // (forceSimpleMode shows Year as text input, Make/Family/Model/Description are hidden)
    const assetValue = this.formControlAsset.value;
    const isAssetValid = assetValue && assetValue.category && assetValue.type && assetValue.year;
    
    // Custom validation: at least one ABN age source is required
    const hasBusinessLookup = this.formControlBusiness.value && 
                              this.formControlBusiness.value.type === 'search-result' && 
                              this.formControlBusiness.value.result?.ABNAgeMonths !== undefined;
    // Use getRawValue() to check value even if field is disabled
    const hasAbnAgeField = this.formControlAbnAge.getRawValue() !== null && this.formControlAbnAge.getRawValue() !== undefined;
    
    if (!hasBusinessLookup && !hasAbnAgeField) {
      this.applicationDialogService.openAlertDialog({
        message: 'ABN Age Required',
        subMessage: 'Please either search for a business using ABN/ACN lookup or manually enter the ABN age in months.',
      });
      return;
    }
    
    if (this.formGroupCalculator.invalid || !isAssetValid) {
      this.applicationDialogService.openAlertDialog({
        message: 'Missing Information',
        subMessage: 'Make sure all fields across tabs are filled in.',
      });
      return;
    }

    // Validate Year field (it's now a text input, so we need to validate the format and range)
    const asset = this.formControlAsset.value;
    if (asset && asset.year) {
      const assetYear = parseInt(asset.year);
      const currentYear = new Date().getFullYear();
      
      if (isNaN(assetYear) || assetYear < 1900 || assetYear > currentYear + 1) {
        this.applicationDialogService.openAlertDialog({
          message: 'Invalid Year',
          subMessage: `Please enter a valid year between 1900 and ${currentYear + 1}.`,
        });
        return;
      }
    }

    // Validate End of Term (EOT) age
    const loanTerms = parseInt(this.formControlLoanTerms.value?.type ?? '0');
    
    if (asset && loanTerms && asset.year && asset.category && asset.category.index) {
      const assetYear = parseInt(asset.year);
      if (!isNaN(assetYear) && !isNaN(loanTerms) && assetYear > 0 && loanTerms > 0) {
        const validation = validateEOTAge(assetYear, asset.category.index, loanTerms, asset.type?.index);
        
        if (!validation.isValid && validation.errorMessage) {
          this.applicationDialogService.openAlertDialog({
            message: `End of Term Validation Error`,
            subMessage: validation.errorMessage
          });
          return;
        }
      }
    }

    this.subscriptions.push(this.applicationDialogService.openAssetQuotationDialog({
      loadFn: (date) => {
        return this.calculateEstimation(date);
      },
      isQuote: true,
      paymentChartHideBalloonPayment: true,
      paymentChartHideBrokerOriginationFee: false,
      paymentChartHideBrokerage: false,
      showPaymentChart: true,
      showArmotizationChart: true,
      showRepaymentEstimation: true,
      showArmotisationSchedule: true,
    }).afterClosed()
      .pipe(
        tap(r => {
          if (r && r.acceptedQuotation) {
            const quoteData = this.saveQuoteData();
            this.appService.saveQuote(quoteData).subscribe(async res => {
              if (res.status) {
                await this.router.navigate(navigationUrlForApplications())
              } else {
                this.applicationDialogService.openAlertDialog({
                  message: `Error`,
                  // subMessage: " Couldn\'t save quote. Try again later."
                  subMessage: "Something is wrong. Try again."
                });
              }
            })
          }
        })
      ).subscribe());
  }

  private saveQuoteData(): Quote {
    // NOTE: formGroupCalculator will be valid when this is called, formControl values will not be null
    const loanCalValues = this.formGroupCalculator.getRawValue();
    const brokerageValue: BrokerageSelectionValue = this.formControlBrokerage.value;
    const loanAmount = this.formControlLoanAmount.value!;
    const quoteValue: Quote = {
      LoanAmount: (loanCalValues.invoiceAmount ?? '0').toString(),
      RepaymentFrequency: loanCalValues.paymentFrequency?.type!,
      LoanTerm: parseInt(loanCalValues.loanTerms?.type ?? '0'),
      Residual: parseInt(loanCalValues.balloonPayment?.type ?? '0'),
      BrokeragePercentage: parseFloat(loanCalValues.brokerage?.type ?? '0'),
      BrokerageAmount: new AppCalculator().calculateBrokerageAmount(this.formControlLoanAmount.value ?? 0,Number(brokerageValue?.type ?? '0')),
      QuoteAsJson: encodeURI(JSON.stringify(loanCalValues)),
    };
    return quoteValue
  }

  private calculateEstimation(date: Moment): TotalPaymentBreakupDialogData {
    // NOTE: formGroupCalculator will be valid when this is called, formControl values will not be null
    const paymentFrequencyValue: PaymentFrequencyValue =  this.formControlPaymentFrequency.value;
    const assetConditionValue: AssetConditionValue = this.formControlAssetCondition.value;
    const assetSelectionValue: AssetSelectionComponentValue = this.formControlAsset.value;
    const loanTermValue: LoanTermValue = this.formControlLoanTerms.value;
    const financeTypeValue: FinanceTypeValue = this.formControlFinanceType.value;
    const brokerageValue: BrokerageSelectionValue = this.formControlBrokerage.value;
    const balloonPaymentValue: BalloonPaymentValue = this.formControlBalloonPayment.value;
    const businessSearchValue: BusinessSearchValue = this.createBusinessSearchValue();
    const bureauReport = businessSearchValue?.result;
    // Year is now a required text input field (forceSimpleMode)
    const currentYear = new Date().getFullYear();
    const assetYear = assetSelectionValue?.year ? parseFloat(assetSelectionValue.year) : currentYear;
    const rst = calculateAssetFinanceEstimation(date, {
      type: 'AssetFinance',
      paymentFrequencyType: paymentFrequencyValue?.type ?? null,
      assetConditionType: assetConditionValue?.type ?? null,
      assetSelectionValue: assetSelectionValue!,
      assetYear: assetYear,
      loanTerm: numeral(loanTermValue?.type).value() ?? 0 ,
      financeType: financeTypeValue?.type ?? null,
      loanAmount: numeral(this.formControlLoanAmount.value).value() ?? 0,
      businessSearchValue: businessSearchValue,
      existingApplicationBureauReport: bureauReport!,
      propertyOwnership: this.formControlPropertyOwner.value!,
      docFeeFinanced: this.formControlDocFeeFinanced.value!,
      brokerOriginationFee: this.formControlBrokerOriginationFee.value!,
      brokerage: Number(brokerageValue?.type ?? 0),
      adverseOnFile: this.formControlAdverseOnFile.value!,
      equifaxDirectorScoreMeetsProductThreshold: this.formControlEquifaxScoreAboveThreshold.value!,
      equifaxDirectorScoreAbove700: this.formControlEquifaxDirectorScoreAbove700.value!,
      privateSaleOrLeaseback: this.formControlTransactionType.value?.type !== 'Dealer Sale',
      balloonPayment: numeral(balloonPaymentValue?.type).value() ?? 0 ,
      rateCard: this.rateCard,
      invoiceAmount: this.formControlInvoiceAmount.value!,
      deposit: this.formControlDepositAmount.value!,
      transactionType: this.formControlTransactionType?.value?.type,
    })

    return rst.totalPaymentBreakupDialogData;
  }

  createBusinessSearchValue(): BusinessSearchResultValue {
    // Priority 1: If business search has been used with ABN age, return the actual data
    const businessValue = this.formControlBusiness.value;
    if (businessValue && businessValue.type === 'search-result' && businessValue.result?.ABNAgeMonths !== undefined) {
      
      console.log('Using ABN age from business lookup:', businessValue.result.ABNAgeMonths);
      return businessValue as BusinessSearchResultValue;
    }
    
    // Priority 2: Use manually entered ABN age field (use getRawValue() to get value even if disabled)
    const manualAbnAge = this.formControlAbnAge.getRawValue() ?? 0;
    if (manualAbnAge > 0) {
      console.log('Using manually entered ABN age:', manualAbnAge);
      return {
        type: 'search-result',
        result: {
          ABNAgeMonths: manualAbnAge,
          GSTAgeMonths: manualAbnAge // Use same value for GST age when manual entry
        } as SearchCompanyByABNResult
      } as BusinessSearchResultValue;
    }
    

    return {
      type: 'search-result',
      result: {
        ABNAgeMonths: 1,
        GSTAgeMonths: 1
      } as SearchCompanyByABNResult
    } as BusinessSearchResultValue;
    
  }

  onCalculateEstimationInterestRate(event: Event) {
    // Trigger mark subject to show validation errors on Interest section components
    this.markTriggerSubjectInterest.next(true);
    
    // Mark all Interest section form controls as touched to trigger validation display
    this.formControlInterestRate.markAsTouched();
    this.formControlFinanceTypeInterest.markAsTouched();
    this.formControlDocFeeFinancedInterest.markAsTouched();
    this.formControlLoanTermsInterest.markAsTouched();
    this.formControlPaymentFrequencyInterest.markAsTouched();
    this.formControlInvoiceAmountInterest.markAsTouched();
    this.formControlDepositAmountInterest.markAsTouched();
    this.formControlBalloonPaymentInterest.markAsTouched();
    this.formControlBrokerOriginationFeeInterest.markAsTouched();
    this.formControlBrokerageInterest.markAsTouched();
    
    // Validate manual interest rate
    const manualInterestRate = this.formControlInterestRate.value;
    const isInterestRateValid = manualInterestRate && manualInterestRate > 0;
    
    // Check if all Interest section form controls are valid
    const isInterestSectionValid = 
      this.formControlFinanceTypeInterest.valid &&
      this.formControlDocFeeFinancedInterest.valid &&
      this.formControlLoanTermsInterest.valid &&
      this.formControlPaymentFrequencyInterest.valid &&
      this.formControlInvoiceAmountInterest.valid &&
      this.formControlDepositAmountInterest.valid &&
      this.formControlBalloonPaymentInterest.valid &&
      this.formControlBrokerOriginationFeeInterest.valid &&
      this.formControlBrokerageInterest.valid;
    
    // Validate all required fields
    if (!isInterestRateValid || !isInterestSectionValid ) {
      this.applicationDialogService.openAlertDialog({
        message: 'Missing Information',
        subMessage: 'Make sure all required fields are filled in correctly.',
      });
      return;
    }
    
    // All validations passed, proceed with calculation
    this.subscriptions.push(this.applicationDialogService.openAssetQuotationDialog({
      loadFn: (date) => {
        // Calculate with manual interest rate using Interest section form controls
        const paymentFrequencyValue: PaymentFrequencyValue =  this.formControlPaymentFrequencyInterest.value;
        const loanTermValue: LoanTermValue = this.formControlLoanTermsInterest.value;
        const financeTypeValue: FinanceTypeValue = this.formControlFinanceTypeInterest.value;
        const brokerageValue: BrokerageSelectionValue = this.formControlBrokerageInterest.value;
        const balloonPaymentValue: BalloonPaymentValue = this.formControlBalloonPaymentInterest.value;
        
        // Calculate loan amount for interest section
        const invoiceAmountInterest = this.formControlInvoiceAmountInterest.value ?? 0;
        const depositAmountInterest = this.formControlDepositAmountInterest.value ?? 0;
        const financeType = this.formControlFinanceTypeInterest.value;
        const depositAmountExGST = Math.round(depositAmountInterest * 1000 / 11) / 100;
        const deposit = financeType?.type === 'rental' ? depositAmountExGST : depositAmountInterest;
        const loanAmountInterest = parseFloat((invoiceAmountInterest - deposit > 0) ? (invoiceAmountInterest - deposit).toFixed(2) : '0');
        
        // Use simple asset selection for manual interest rate calculation
        const currentYear = new Date().getFullYear();
        const simpleAssetSelection = {
          category: { index: '130', value: 'Other' },
          type: { index: '0', value: 'Other' },
          year: currentYear.toString(),
          invalid: false
        };
        
        const calculationParamsInterest = {
          type: 'AssetFinance' as const,
          paymentFrequencyType: paymentFrequencyValue?.type ?? null,
          assetConditionType: 'Used' as const,
          assetSelectionValue: simpleAssetSelection as any,
          assetYear: currentYear,
          loanTerm: numeral(loanTermValue?.type).value() ?? 0,
          financeType: financeTypeValue?.type ?? null,
          loanAmount: loanAmountInterest,
          businessSearchValue: null,
          existingApplicationBureauReport: null,
          propertyOwnership: true,
          docFeeFinanced: this.formControlDocFeeFinancedInterest.value!,
          brokerOriginationFee: this.formControlBrokerOriginationFeeInterest.value!,
          brokerage: Number(brokerageValue?.type ?? 0),
          adverseOnFile: false,
          equifaxDirectorScoreMeetsProductThreshold: true,
          privateSaleOrLeaseback: false,
          balloonPayment: numeral(balloonPaymentValue?.type).value() ?? 0,
          rateCard: this.rateCard,
          invoiceAmount: invoiceAmountInterest,
          deposit: depositAmountInterest,
          transactionType: 'Dealer Sale' as const,
          manualInterestRate: manualInterestRate,
        };
        
        const rst = calculateAssetFinanceEstimation(date, calculationParamsInterest)
        return rst.totalPaymentBreakupDialogData;
      },
      isQuote: true,
      paymentChartHideBalloonPayment: true,
      paymentChartHideBrokerOriginationFee: false,
      paymentChartHideBrokerage: false,
      showPaymentChart: true,
      showArmotizationChart: true,
      showRepaymentEstimation: true,
      showArmotisationSchedule: true,
    }).afterClosed()
      .pipe(
        tap(r => {
          if (r && r.acceptedQuotation) {
            const quoteData = this.saveQuoteData();
            this.appService.saveQuote(quoteData).subscribe(async res => {
              if (res.status) {
                await this.router.navigate(navigationUrlForApplications())
              } else {
                this.applicationDialogService.openAlertDialog({
                  message: `Error`,
                  subMessage: "Something is wrong. Try again."
                });
              }
            })
          }
        })
      ).subscribe());
  }
}
